'use client'

import { useEffect, useState } from 'react'
import { M } from '../lesson/Math'
import { Controls, fmt, Readout, Segmented, Slider, usePrefersReducedMotion } from './controls'

// ─── Variables as labelled boxes ───────────────────────────────────────────────

export function VariableBoxes({ items }: { items: { name: string; value: string; type: string; kind?: 'const' | 'let' }[] }) {
  return (
    <div className="flex flex-wrap gap-4 py-2">
      {items.map(it => (
        <div key={it.name} className="min-w-[132px]">
          <div className="flex items-baseline justify-between gap-2 px-1">
            <span className="gd-mono text-[13px] font-semibold" style={{ color: 'var(--gd-ink)' }}>
              {it.name}
            </span>
            <span className="gd-label !text-[9.5px]">{it.kind ?? 'const'}</span>
          </div>
          <div
            className="mt-1 flex h-[64px] items-center justify-center rounded-[10px] border-2 px-4"
            style={{ borderColor: 'var(--gd-ink)', background: 'var(--gd-bg)', boxShadow: 'inset 0 -6px 0 var(--gd-bg-2)' }}
          >
            <span className="gd-mono text-[18px]" style={{ color: it.type === 'string' ? 'var(--gd-green)' : it.type === 'boolean' ? 'var(--gd-violet)' : 'var(--gd-blue)' }}>
              {it.value}
            </span>
          </div>
          <div className="gd-label mt-1.5 px-1 !text-[9.5px]">type: {it.type}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Function machine ──────────────────────────────────────────────────────────

const MACHINES = [
  { id: 'double', label: 'double', tex: 'f(x) = 2x', f: (x: number) => 2 * x, code: 'x => 2 * x' },
  { id: 'plus3', label: 'add 3', tex: 'f(x) = x + 3', f: (x: number) => x + 3, code: 'x => x + 3' },
  { id: 'square', label: 'square', tex: 'f(x) = x^2', f: (x: number) => x * x, code: 'x => x * x' },
  { id: 'line', label: '2x + 1', tex: 'f(x) = 2x + 1', f: (x: number) => 2 * x + 1, code: 'x => 2 * x + 1' },
] as const

/** Input ball → machine → output ball, with the rule visible on the side. */
export function FunctionMachine() {
  const [which, setWhich] = useState<(typeof MACHINES)[number]['id']>('line')
  const [x, setX] = useState(3)
  const [tick, setTick] = useState(0)
  const reduced = usePrefersReducedMotion()
  const m = MACHINES.find(mm => mm.id === which)!
  const y = m.f(x)

  useEffect(() => {
    const t = setTimeout(() => setTick(k => k + 1), 0)
    return () => clearTimeout(t)
  }, [x, which])

  return (
    <div>
      <div className="relative mx-auto flex max-w-[620px] items-center justify-between gap-1.5 py-6 sm:gap-3">
        <div key={`in-${tick}`} className="flex flex-col items-center" style={{ animation: reduced ? undefined : 'gd-fm-in 0.6s var(--gd-ease) both' }}>
          <span className="gd-label !text-[10px]">input</span>
          <span className="mt-2 flex h-14 w-14 items-center justify-center rounded-full text-[19px] font-semibold sm:h-16 sm:w-16 sm:text-[22px]" style={{ background: 'color-mix(in srgb, var(--gd-blue) 15%, transparent)', color: 'var(--gd-blue)', border: '2px solid var(--gd-blue)' }}>
            {fmt(x, 1)}
          </span>
        </div>
        <svg viewBox="0 0 60 20" className="h-5 w-6 shrink-0 sm:w-[60px]" preserveAspectRatio="none" aria-hidden>
          <path d="M0 10 H50 M44 4 L52 10 L44 16" stroke="var(--gd-ink-3)" strokeWidth="2" fill="none" />
        </svg>
        <div className="relative flex h-[120px] min-w-0 flex-1 flex-col items-center justify-center rounded-[16px] border-2 px-2 text-center sm:min-w-[180px]" style={{ borderColor: 'var(--gd-ink)', background: 'var(--gd-surface)' }}>
          <span className="gd-label !text-[10px]">the machine</span>
          <span className="mt-2 text-[20px]">
            <M>{m.tex}</M>
          </span>
          <span className="gd-mono mt-1 text-[12px]" style={{ color: 'var(--gd-ink-3)' }}>
            {m.code}
          </span>
          <span key={`gear-${tick}`} className="absolute -top-3 right-4 text-[22px]" style={{ animation: reduced ? undefined : 'gd-fm-spin 0.7s var(--gd-ease)' }} aria-hidden>
            ⚙
          </span>
        </div>
        <svg viewBox="0 0 60 20" className="h-5 w-6 shrink-0 sm:w-[60px]" preserveAspectRatio="none" aria-hidden>
          <path d="M0 10 H50 M44 4 L52 10 L44 16" stroke="var(--gd-ink-3)" strokeWidth="2" fill="none" />
        </svg>
        <div key={`out-${tick}`} className="flex flex-col items-center" style={{ animation: reduced ? undefined : 'gd-fm-out 0.7s var(--gd-ease) 0.25s both' }}>
          <span className="gd-label !text-[10px]">output</span>
          <span className="mt-2 flex h-14 w-14 items-center justify-center rounded-full text-[19px] font-semibold sm:h-16 sm:w-16 sm:text-[22px]" style={{ background: 'color-mix(in srgb, var(--gd-amber) 15%, transparent)', color: 'var(--gd-amber)', border: '2px solid var(--gd-amber)' }}>
            {fmt(y, 1)}
          </span>
        </div>
      </div>
      <Controls>
        <Segmented label="Choose a machine" value={which} onChange={setWhich} options={MACHINES.map(mm => ({ value: mm.id, label: mm.label }))} />
        <div className="min-w-[240px] flex-1">
          <Slider label={<M>{'\\blue{x}'}</M>} value={x} onChange={setX} min={-5} max={5} step={0.5} role="blue" digits={1} />
        </div>
      </Controls>
      <style>{`
        @keyframes gd-fm-in { from { opacity: 0; transform: translateX(-24px) } }
        @keyframes gd-fm-out { from { opacity: 0; transform: translateX(-24px) scale(0.6) } }
        @keyframes gd-fm-spin { from { transform: rotate(0) } to { transform: rotate(180deg) } }
      `}</style>
    </div>
  )
}

// ─── Loop stepper: watch a for-loop walk an array ─────────────────────────────

export function LoopStepper({ values = [4, 8, 15, 16, 23, 42] }: { values?: number[] }) {
  const [i, setI] = useState(-1)
  const [playing, setPlaying] = useState(false)
  const [mode, setMode] = useState<'sum' | 'max'>('sum')
  const reduced = usePrefersReducedMotion()

  useEffect(() => {
    if (!playing) return
    if (i >= values.length - 1) {
      const t = setTimeout(() => setPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setI(k => k + 1), reduced ? 150 : 750)
    return () => clearTimeout(t)
  }, [playing, i, values.length, reduced])

  const seen = values.slice(0, i + 1)
  const acc = mode === 'sum' ? seen.reduce((s, v) => s + v, 0) : seen.length ? Math.max(...seen) : -Infinity
  const accName = mode === 'sum' ? 'total' : 'best'
  const code =
    mode === 'sum'
      ? ['let total = 0', 'for (let i = 0; i < xs.length; i++) {', '  total = total + xs[i]', '}']
      : ['let best = -Infinity', 'for (let i = 0; i < xs.length; i++) {', '  if (xs[i] > best) best = xs[i]', '}']

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 py-3">
        {values.map((v, k) => (
          <div key={k} className="flex flex-col items-center">
            <span className="gd-mono text-[11px]" style={{ color: k === i ? 'var(--gd-accent)' : 'var(--gd-ink-3)' }}>
              [{k}]
            </span>
            <span
              className="mt-1 flex h-14 w-14 items-center justify-center rounded-[10px] border-2 text-[18px] font-semibold transition-all duration-300"
              style={{
                borderColor: k === i ? 'var(--gd-accent)' : 'var(--gd-rule-2)',
                background: k <= i ? 'color-mix(in srgb, var(--gd-blue) 12%, var(--gd-surface))' : 'var(--gd-surface)',
                color: 'var(--gd-blue)',
                transform: k === i ? 'translateY(-6px)' : 'none',
              }}
            >
              {v}
            </span>
          </div>
        ))}
        <div className="ml-4 flex flex-col items-center">
          <span className="gd-mono text-[11px]" style={{ color: 'var(--gd-ink-3)' }}>
            {accName}
          </span>
          <span className="mt-1 flex h-14 min-w-14 items-center justify-center rounded-[10px] border-2 px-3 text-[18px] font-semibold" style={{ borderColor: 'var(--gd-amber)', color: 'var(--gd-amber)' }}>
            {Number.isFinite(acc) ? acc : '−∞'}
          </span>
        </div>
      </div>
      <pre className="gd-pre !my-3 !text-[13.5px]">
        {code.map((line, k) => (
          <div key={k} style={{ background: (k === 2 && i >= 0) || (k === 1 && i >= 0 && i < values.length) ? 'color-mix(in srgb, var(--gd-accent) 9%, transparent)' : undefined }}>
            {line}
          </div>
        ))}
      </pre>
      <Controls>
        <Segmented label="Loop" value={mode} onChange={v => { setMode(v); setI(-1); setPlaying(false) }} options={[{ value: 'sum', label: 'Add them up' }, { value: 'max', label: 'Find the biggest' }]} />
        <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => setI(k => Math.min(values.length - 1, k + 1))} disabled={i >= values.length - 1}>
          Step
        </button>
        <button type="button" className="gd-btn gd-btn-ink gd-btn-xs" onClick={() => { if (i >= values.length - 1) setI(-1); setPlaying(p => !p) }}>
          {playing ? 'Pause' : i >= values.length - 1 ? 'Replay' : 'Play'}
        </button>
        <button type="button" className="gd-btn gd-btn-ghost gd-btn-xs" onClick={() => { setI(-1); setPlaying(false) }}>
          Reset
        </button>
        <Readout label="i" value={i < 0 ? '—' : i} role="accent" />
      </Controls>
    </div>
  )
}
